import gql from "graphql-tag";
import { platform } from "~/platform/socket";
import { deeplinkRegistryStore, setDeeplinkRegistryStore } from "./store";

interface WidgetDeeplinkRegistry {
	[key: string]: {
		items: {
			path: string;
			item: string;
		}[];
		queryParams: string[];
	};
}

interface ExternalDeeplink {
	path: string;
	queryParams: string[];
	groupId: string;
}

export function setDeeplink(internalLink: string, external: ExternalDeeplink): void {
	setDeeplinkRegistryStore("registry", internalLink, external);
}

export function getDeeplink(internalLink: string): (ExternalDeeplink & { internal: string }) | undefined {
	const external = Object.entries(deeplinkRegistryStore.registry ?? {}).find(([deeplink, external]) => {
		if (internalLink === deeplink) {
			return true;
		}

		const internalParts = internalLink.split("/");

		for (const _ of external.queryParams) {
			internalParts.pop();
		}

		return deeplink === internalParts.join("/");
	});

	if (!external) {
		return;
	}

	return {
		...external[1],
		internal: external[0],
	};
}

export function removeDeeplink(groupId: string): void {
	setDeeplinkRegistryStore("registry", (registry) => {
		const keysToDelete = Object.keys(registry).filter((key) => registry[key].groupId === groupId);

		for (const key of keysToDelete) {
			delete registry[key];
		}

		return registry;
	});
}

export function clearDeeplinks(): void {
	setDeeplinkRegistryStore("registry", {});
}

export async function updateDeeplinkRegistry(): Promise<void> {
	const query = gql`
		query ($modifiedAt: DateTime) {
			webWidgetsDeeplinkRegistry(modifiedAt: $modifiedAt) {
				modified
				deleted
			}
		}
	`;

	const variables = {
		modifiedAt: deeplinkRegistryStore.lastUpdate || undefined,
	};

	interface Response {
		webWidgetsDeeplinkRegistry: {
			modified: WidgetDeeplinkRegistry;
			deleted: string[];
		};
	}

	const {
		webWidgetsDeeplinkRegistry: { deleted, modified },
	} = await platform.query<Response>(query, variables);

	for (const item of deleted) {
		removeDeeplink(item);
	}

	for (const id in modified) {
		const group = modified[id];
		for (const item of group.items) {
			setDeeplink(item.path, {
				path: item.item,
				queryParams: group.queryParams,
				groupId: id,
			});
		}
	}

	const now = new Date();
	setDeeplinkRegistryStore("lastUpdate", now.toISOString());
}
